import InfoIcon from "@mui/icons-material/Info";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import { Observer } from "mobx-react";
import SoundClip from "models/SoundClip";
import React, { useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { InteractionType } from "../../../../models/InteractionType";
import { useStore } from "../../../../utils/useStore";
import { EvalStore } from "../../Stores/EvalStore";
import { isTextMediaType } from "../EvalPage";
import ClipView from "./ClipView";
import EmptyClipList from "./EmptyClipList";
import AcxSwitch from "components/UI/AcxSwitch";
import hexToRGB from "utils/hexToRGB";

interface ClipViewProps {
    drawerPixelOffset: number;
    interactionType?: InteractionType;
}

export default function ClipList(props: ClipViewProps) {
    const store = useStore(EvalStore);

    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [allClipsOpen, setAllClipsOpen] = useState(false);

    const toggleClipsOpen = () => {
        setAllClipsOpen(!allClipsOpen);
        if (store.openClipId) {
            store.setOpenClipId();
        }
    };

    const onDeleteButton = (soundClip: SoundClip) => {
        store.stageDeleteClip = soundClip;
        setDialogIsOpen(true);
    };

    const onDelete = () => {
        if (store.stageDeleteClip) {
            //store.currentEval?.deleteClip(store.stageDeleteClip);
            store.deleteClip(store.stageDeleteClip);
        }
        onDialogClose();
    };

    const onDialogClose = () => {
        setDialogIsOpen(false);
    };

    const onExit = () => {
        store.stageDeleteClip = null;
    };

    return (
        <Observer>
            {() => (
                <Paper
                    square
                    sx={{
                        width: "100%",
                        boxShadow: "-2px 0 2px -2px #888",
                        height: `100%`,
                        overflowX: "hidden",
                        overflowY: "hidden",
                        minWidth: "141px",
                    }}
                >
                    <Grid
                        container
                        justifyContent="space-between"
                        sx={{
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            paddingBottom: "12px",
                            paddingTop: "6px",
                        }}
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography variant="h2" gutterBottom={true}>
                                Clips
                            </Typography>
                        </Grid>

                        <Grid item>
                            <IconButton
                                size="small"
                                onClick={toggleClipsOpen}
                                sx={{
                                    color: hexToRGB("#000000", 0.54),
                                }}
                            >
                                {allClipsOpen ? (
                                    <Tooltip title="Collapse">
                                        <ChevronDownIcon />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Expand">
                                        <ChevronUpIcon />
                                    </Tooltip>
                                )}
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        sx={{
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            paddingBottom: "12px",
                        }}
                    >
                        <Tooltip title="When enabled, adding a tag to a clip will automatically add that clip to the related Eval Question.">
                            <FormControlLabel
                                control={
                                    <AcxSwitch
                                        checked={store.enableAutoBindClips}
                                        onChange={(val) => {
                                            store.enableAutoBindClips =
                                                val.target.checked;
                                        }}
                                        name="EnableAutoBindClips"
                                        id="EnableAutoBindClips"
                                    />
                                }
                                label={
                                    <Grid container alignItems="center">
                                        <Grid
                                            item
                                            style={{ paddingRight: "8px" }}
                                        >
                                            Sync tags with the evaluation
                                        </Grid>
                                        <InfoIcon
                                            color="disabled"
                                            fontSize="small"
                                        />
                                    </Grid>
                                }
                            />
                        </Tooltip>
                    </Grid>
                    <Divider variant={"fullWidth"} />

                    <div
                        style={{
                            overflowX: "hidden",
                            height: `calc(100% - ${
                                isTextMediaType(props.interactionType)
                                    ? 50
                                    : props.drawerPixelOffset + 125 // accounting for the top of clip list
                            }px)`,
                        }}
                    >
                        <Scrollbars autoHide>
                            {(store.currentEval?.activeSoundClips?.length ??
                                0) > 0 ? (
                                store.currentEval?.activeSoundClips?.map(
                                    (soundClip, index) => (
                                        <React.Fragment
                                            key={`${soundClip.id}-${index}`}
                                        >
                                            <ClipView
                                                soundClip={soundClip}
                                                onDeleteButton={() => {
                                                    onDeleteButton(soundClip);
                                                }}
                                                isOpen={allClipsOpen}
                                                index={index}
                                            />
                                            <Divider />
                                        </React.Fragment>
                                    ),
                                )
                            ) : (
                                <EmptyClipList />
                            )}
                        </Scrollbars>
                        <AcxConfirmationDialog
                            isOpen={dialogIsOpen}
                            fullWidth
                            onClose={onDialogClose}
                            onCancel={onDialogClose}
                            onConfirm={onDelete}
                            title={`Are you sure you want to delete ${store.stageDeleteClip?.segmentName}?`}
                            onTransitionExit={onExit}
                        />
                    </div>
                </Paper>
            )}
        </Observer>
    );
}
